import React, { useState, useEffect, useRef } from 'react';
import { canReturnTo, homePathForRole } from '@/lib/auth/roles';
import { forgetRefreshSession } from '@/api/session-refresh';
import { isAxiosError } from 'axios';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Icon } from '@/lib/icons';
import { useAuthActions } from '@/hooks/use-auth';
import { useLoginWelcome } from '@/hooks/use-login-welcome';
import {
  AuthShell,
  AuthHeading,
  AuthNotice,
  AuthSpinner,
  AuthPasswordToggle,
  authShellCopy,
  AUTH_FIELD_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from '@/features/auth-shell';
import { authApi } from '@/api/auth.api';
import { translations } from '@/translations/translations';
import { consumeAuthReturnPath } from '@/lib/auth-return';

type ResolvedLocationState = { from: string };

const resolveLocationState = (value: unknown): ResolvedLocationState | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as { from?: unknown };
  if (typeof candidate.from === 'string') {
    return { from: candidate.from };
  }

  return null;
};

export default function Login() {
  const { setUser, isAuthenticated, user } = useApp();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { toast } = useToast();
  const { login, loading } = useAuthActions();
  const greet = useLoginWelcome();
  const currentLang = lang || 'fr';
  const t = translations[currentLang as 'fr' | 'en'] || translations.fr;

  const location = useLocation();
  const locationState = React.useMemo(() => resolveLocationState(location.state), [location.state]);
  const from = locationState?.from;

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const hasRedirectedRef = useRef(false);

  const shouldSkipPolicy = (candidateUser?: { isAcceptedTerms?: boolean } | null) => {
    return localStorage.getItem('yodi-policy-accepted') === 'true' || candidateUser?.isAcceptedTerms === true;
  };

  // After signing in: the page the person was on if their profile may open
  // it, else their own space; the data policy first when not yet accepted.
  const signedInTarget = (signedIn: { role?: unknown } | null | undefined, policyUser?: { isAcceptedTerms?: boolean } | null) => {
    const remembered = consumeAuthReturnPath() ?? from;
    const home = homePathForRole(signedIn?.role, currentLang);
    const destination = remembered && canReturnTo(remembered, signedIn?.role, currentLang) ? remembered : home;
    return shouldSkipPolicy(policyUser ?? undefined)
      ? destination
      : `/${currentLang}/data-policy?next=${encodeURIComponent(destination)}`;
  };

  // Bounce already-authenticated visitors away from the login page.
  // Guarded by hasRedirectedRef so a successful submit never gets overridden
  // by this effect racing against the login flow.
  useEffect(() => {
    if (isAuthenticated && !hasRedirectedRef.current) {
      navigate(homePathForRole(user?.role, currentLang), { replace: true });
    }
  }, [currentLang, isAuthenticated, navigate, user?.role]);

  // Gérer le callback Google
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (token) {
        setLoadingGoogle(true);
        try {
          // Sauvegarder le token
          forgetRefreshSession();
          localStorage.setItem('yodi-token', token);
          localStorage.removeItem('token');

          // Récupérer les infos utilisateur
          const user = await authApi.getCurrentUser();
          setUser(user);

          greet(user, urlParams.get('firstLogin') === '1' ? true : undefined);

          // Nettoyer l'URL
          window.history.replaceState({}, document.title, window.location.pathname);

          // Rediriger
          hasRedirectedRef.current = true;
          navigate(signedInTarget(user, user as { isAcceptedTerms?: boolean }), { replace: true });
        } catch (error) {
          toast({
            title: 'Erreur',
            description: 'Impossible de récupérer vos informations',
            variant: 'destructive',
          });
        } finally {
          setLoadingGoogle(false);
        }
      }
    };

    handleGoogleCallback();
  }, [location.search]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // The welcome toast or dialog is raised by useAuthActions().login.
      const session = await login(loginForm);

      let target: string;
      if (session?.user?.mustChangePassword) {
        target = `/${currentLang}/force-change-password`;
      } else {
        target = signedInTarget(session?.user, session?.user);
      }

      hasRedirectedRef.current = true;
      navigate(target, { replace: true });
    } catch (err: unknown) {
      const apiMessage = isAxiosError(err) ? err.response?.data?.message : undefined;
      const normalizedMessage = typeof apiMessage === 'string' ? apiMessage.toLowerCase() : '';
      const accountStatus = normalizedMessage.includes('banned')
        ? 'banned'
        : normalizedMessage.includes('suspended')
          ? 'suspended'
          : null;

      if (accountStatus) {
        navigate('/' + currentLang + '/account-status?status=' + accountStatus, { replace: true });
        return;
      }

      console.error(err);
    }
  };

  const handleGoogleLogin = () => {
    setLoadingGoogle(true);
    authApi.initiateGoogleLogin();
  };

  if (loadingGoogle) {
    return (
      <AuthShell documentTitle={t.login}>
        <AuthHeading title={t.login} />
        <AuthNotice
          icon={<AuthSpinner className="size-5 text-brand" />}
          title={authShellCopy(currentLang).googlePending}
          testId="login-google-pending"
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell documentTitle={t.login}>
      <AuthHeading title={t.login} description={t.authContinueContribution} />

      {/* Connexion Google : désactivée en attendant que le SSO soit réactivé.
          handleGoogleLogin reste branché pour le jour où le bouton revient. */}

      <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium">
            {t.loginIdentifier}
          </Label>
          <div className="relative">
            <Icon
              name="userCheck"
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="username"
              type="text"
              autoComplete="username"
              placeholder={t.usernamePlaceholder}
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              required
              className={`pl-10 ${AUTH_FIELD_CLASS}`}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="password" className="text-sm font-medium">
              {t.password}
            </Label>
            <Link
              to={`/${lang || 'fr'}/forgot-password`}
              className="rounded text-sm font-medium text-brand hover:underline focus-ring"
            >
              {t.forgotPassword}
            </Link>
          </div>
          <div className="relative">
            <Icon
              name="lock"
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              required
              className={`pl-10 pr-11 ${AUTH_FIELD_CLASS}`}
            />
            <AuthPasswordToggle visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
          </div>
        </div>

        <Button type="submit" disabled={loading} className={AUTH_PRIMARY_BUTTON_CLASS}>
          {loading ? (
            <span className="flex items-center gap-2">
              <AuthSpinner />
              <span>{t.loggingIn}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span>{t.connect}</span>
              <Icon name="arrowRight" className="h-4 w-4" />
            </span>
          )}
        </Button>
      </form>

      <div className="space-y-4 border-t border-border/70 pt-6 text-center">
        <p className="text-sm text-muted-foreground">
          {t.noAccount}{' '}
          <Link to={`/${lang || 'fr'}/signup`} className="rounded font-semibold text-brand hover:underline focus-ring">
            {t.signupLink}
          </Link>
        </p>
        <p className="text-xs leading-relaxed text-faint-foreground">{t.acceptTerms}</p>
      </div>
    </AuthShell>
  );
}
